import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NativeSelect,
  Stack,
  TextInput,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import {
  useCreateSubscription,
  useTemplates,
  useUpdateSubscription,
  type RecipientRule,
  type SubscriptionDto,
} from '../api/notifications';

const CONSUMER_STREAMS = [
  'case.created',
  'case.workflow.completed',
  'case.images_check.completed',
  'consent.check.completed',
  'ai_review.completed',
  'ai_review.started',
  'ai_review.failed',
  'ai_review.superseded',
  'human_review.completed',
  'human_review.failed',
  'workflow.started',
  'workflow.halted',
  'workflow.cancelled',
] as const;

const _RECIPIENT_TYPES = ['slackChannel', 'emailLiteral', 'email', 'user', 'webhook'] as const;

type RecipientType = (typeof _RECIPIENT_TYPES)[number];

interface Props {
  opened: boolean;
  onClose: () => void;
  initial?: SubscriptionDto;
  orgOptions: Array<{ value: string; label: string }>;
}

function buildRecipientRule(type: RecipientType, value: string): RecipientRule {
  switch (type) {
    case 'slackChannel':
      return { type: 'slackChannel', channel: value };
    case 'emailLiteral':
      return { type: 'emailLiteral', address: value };
    case 'email':
      return { type: 'email', path: value };
    case 'user':
      return { type: 'user', path: value };
    case 'webhook':
      return { type: 'webhook', url: value };
  }
}

function recipientValueFromRule(rule?: RecipientRule): string {
  if (!rule) return '';
  switch (rule.type) {
    case 'slackChannel':
      return rule.channel;
    case 'emailLiteral':
      return rule.address;
    case 'email':
    case 'user':
      return rule.path;
    case 'webhook':
      return rule.url;
  }
}

function recipientFieldLabel(type: RecipientType): string {
  switch (type) {
    case 'slackChannel':
      return 'Slack channel ID';
    case 'emailLiteral':
      return 'Email address';
    case 'email':
      return 'Payload path (email)';
    case 'user':
      return 'Payload path (user ID)';
    case 'webhook':
      return 'Webhook URL';
  }
}

export function SubscriptionModal({ opened, onClose, initial, orgOptions }: Props) {
  const isEdit = !!initial;

  const [eventType, setEventType] = useState<string>(initial?.eventType ?? CONSUMER_STREAMS[0]);
  const [templateCode, setTemplateCode] = useState<string>(initial?.templateCode ?? '');
  const [recipientType, setRecipientType] = useState<RecipientType>(
    (initial?.recipientRule.type as RecipientType) ?? 'slackChannel',
  );
  const [recipientValue, setRecipientValue] = useState<string>(
    initial ? recipientValueFromRule(initial.recipientRule) : '',
  );
  const [orgId, setOrgId] = useState<string>(initial?.orgId ?? '');
  const [enabled, setEnabled] = useState<boolean>(initial?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  // Reset form state when modal reopens
  useEffect(() => {
    if (!opened) return;
    setEventType(initial?.eventType ?? CONSUMER_STREAMS[0]);
    setTemplateCode(initial?.templateCode ?? '');
    setRecipientType((initial?.recipientRule?.type ?? 'slackChannel') as RecipientType);
    setRecipientValue(recipientValueFromRule(initial?.recipientRule));
    setOrgId(initial?.orgId ?? '');
    setEnabled(initial?.enabled ?? true);
    setError('');
  }, [opened]); // intentionally omit `initial` — we want a one-shot reset on open

  const templates = useTemplates();
  const create = useCreateSubscription();
  // id is only '' when initial is undefined; update.mutate is never called in that case
  const update = useUpdateSubscription(initial?.id ?? '');

  const templateOptions = (templates.data ?? []).map((t) => ({
    value: t.code,
    label: t.code,
  }));

  const resolvedTemplateCode = templateCode || (templateOptions[0]?.value ?? '');

  function handleError(err: unknown) {
    if (err instanceof ApiError) {
      setError(
        `Error ${err.status}: ${typeof err.body === 'string' ? err.body : JSON.stringify(err.body)}`,
      );
    } else {
      setError('An unexpected error occurred.');
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const recipientRule = buildRecipientRule(recipientType, recipientValue.trim());

    if (isEdit) {
      update.mutate(
        { templateCode: resolvedTemplateCode, recipientRule, enabled },
        { onSuccess: () => onClose(), onError: handleError },
      );
    } else {
      create.mutate(
        {
          eventType,
          templateCode: resolvedTemplateCode,
          recipientRule,
          ...(orgId ? { orgId } : {}),
          enabled: true,
        },
        { onSuccess: () => onClose(), onError: handleError },
      );
    }
  }

  const mutation = isEdit ? update : create;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit subscription' : 'Add subscription'}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />} role="alert">
              {error}
            </Alert>
          )}

          <NativeSelect
            label="Event type"
            value={eventType}
            onChange={(e) => setEventType(e.currentTarget.value)}
            data={CONSUMER_STREAMS.map((s) => ({ value: s, label: s }))}
          />

          <NativeSelect
            label="Template"
            value={resolvedTemplateCode}
            onChange={(e) => setTemplateCode(e.currentTarget.value)}
            data={
              templates.isError
                ? [{ value: '', label: 'Error loading templates' }]
                : templateOptions.length
                  ? templateOptions
                  : [{ value: '', label: 'Loading…' }]
            }
            disabled={templates.isLoading || templates.isError}
          />

          <NativeSelect
            label="Recipient type"
            value={recipientType}
            onChange={(e) => {
              setRecipientType(e.currentTarget.value as RecipientType);
              setRecipientValue('');
            }}
            data={[
              { value: 'slackChannel', label: 'Slack channel' },
              { value: 'emailLiteral', label: 'Email address (literal)' },
              { value: 'email', label: 'Email (from payload)' },
              { value: 'user', label: 'User (from payload)' },
              { value: 'webhook', label: 'Webhook URL' },
            ]}
          />

          <TextInput
            label={recipientFieldLabel(recipientType)}
            value={recipientValue}
            onChange={(e) => setRecipientValue(e.currentTarget.value)}
            required
          />

          <NativeSelect
            label="Org (optional — blank = global)"
            value={orgId}
            onChange={(e) => setOrgId(e.currentTarget.value)}
            data={[{ value: '', label: '— global —' }, ...orgOptions]}
          />

          {isEdit && (
            <Checkbox
              label="Enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
